import type {
	PreloadDisplayStatus,
	PreloadDisplayStatusKind,
} from "../../../../../Components/StatusPill/preloadStatus"
import type { PrefetchFileEntry, PrefetchSnapshot } from "./types"

const ACTIVE_USAGE_WINDOW_MS = 15 * 60 * 1000

const finiteNumberOrZero = (value: number | null | undefined): number => {
	return typeof value === "number" && Number.isFinite(value) ? value : 0
}

export const derivePreloadStatus = (
	entry?: PrefetchFileEntry
): PreloadDisplayStatus => {
	if (!entry) {
		return { kind: "not_loaded", label: "Not loaded" }
	}

	if (entry.state === "failed") {
		return { kind: "failed", label: "Failed" }
	}
	if (entry.state === "cancelled") {
		return { kind: "cancelled", label: "Cancelled" }
	}
	if (entry.state === "unloading") {
		return { kind: "unloading", label: "Unloading..." }
	}
	if (entry.state === "queued") {
		return { kind: "queued", label: "Queued" }
	}
	if (entry.state === "running") {
		return { kind: "loading", label: `${finiteNumberOrZero(entry.percent_loaded).toFixed(1)}%` }
	}

	return { kind: "loaded", label: "Loaded" }
}

export const buildPreloadStatusMap = (
	files: PrefetchFileEntry[]
): Map<string, PreloadDisplayStatus> => {
	return new Map(files.map((file) => [
		file.data_object_id,
		derivePreloadStatus(file),
	]))
}

export const getQueueStatusLabel = (entry?: PrefetchFileEntry): string | null => {
	if (
		entry?.state !== "queued" ||
		entry.queue_position === null ||
		!Number.isInteger(entry.queue_position) ||
		entry.queue_position < 0
	) {
		return null
	}

	if (entry.queue_position === 0) {
		return "Next in queue"
	}

	return `${entry.queue_position} ${entry.queue_position === 1 ? "file" : "files"} ahead`
}

export const getTableStatusLabel = (entry: PrefetchFileEntry): string => {
	if (entry.state === "failed") {
		return "Failed"
	}
	if (entry.state === "cancelled") {
		return "Cancelled"
	}
	if (entry.state === "unloading") {
		return "Unloading..."
	}
	if (entry.state === "running") {
		return `Loading ${finiteNumberOrZero(entry.percent_loaded).toFixed(2)}%`
	}
	if (entry.state === "queued") {
		return getQueueStatusLabel(entry) ?? "Queued"
	}
	if (entry.state === "complete") {
		const lastUsedSeconds = finiteNumberOrZero(entry.last_used)
		const isRecentlyUsed = lastUsedSeconds > 0 &&
			Date.now() - lastUsedSeconds * 1000 <= ACTIVE_USAGE_WINDOW_MS
		return isRecentlyUsed ? "In use" : "Loaded"
	}
	return "Not in use"
}

export const getStorageSummary = (snapshot: PrefetchSnapshot) => {
	const incomingSize = snapshot.files
		.filter((file) => ["queued", "running", "cancelled"].includes(file.state))
		.reduce((total, file) => total + finiteNumberOrZero(file.size), 0)

	return {
		currentSizeBytes: finiteNumberOrZero(snapshot.storage.current_size) + incomingSize,
		maxSizeBytes: finiteNumberOrZero(snapshot.storage.max_total_size),
	}
}

export const getActiveFile = (
	files: PrefetchFileEntry[]
): PrefetchFileEntry | undefined => {
	return files.find((file) => file.state === "running" || file.state === "cancelled")
		?? files.find((file) => file.state === "queued")
}

export const getEntry = (
	files: PrefetchFileEntry[],
	dataObjectId: number | string
): PrefetchFileEntry | undefined => {
	return files.find((file) => file.data_object_id === String(dataObjectId))
}

export const ACTIVE_PRELOAD_KINDS: PreloadDisplayStatusKind[] = [
	"queued",
	"loading",
	"loaded",
	"cancelled",
]
